import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API, useAuth } from "../../App";
import {
  Activity, ShieldAlert, ShieldCheck, Shield, Flame, AlertTriangle,
  MessageCircle, RefreshCw, Pause, Play, MapPin, Eye,
  Heart, Send, X, ChevronDown, ChevronUp, CheckCircle,
  TrendingUp, Home, Search,
  Power, Reply, MailOpen, MousePointerClick
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { BRAND } from "../../lib/brandConfig";

// ─── Helpers ───────────────────────────────────────
const intentLabel = (s) => (s >= 7 ? "High" : s >= 4 ? "Medium" : "Low");
const intentColor = (s) =>
  s >= 7
    ? "bg-red-100 text-red-700"
    : s >= 4
    ? "bg-amber-100 text-amber-700"
    : "bg-slate-100 text-slate-600";

const attrConfig = {
  protected:        { label: "Protected",          color: "bg-green-100 text-green-700",  icon: ShieldCheck },
  reminded:         { label: "Reminded",           color: "bg-yellow-100 text-yellow-700", icon: Shield },
  at_risk:          { label: "At Risk",            color: "bg-red-100 text-red-700",       icon: ShieldAlert },
  not_acknowledged: { label: "Not Acknowledged",   color: "bg-slate-100 text-slate-600",   icon: AlertTriangle },
};

const stageColors = {
  new: "bg-blue-100 text-blue-700",
  contacted: "bg-cyan-100 text-cyan-700",
  high_intent: "bg-emerald-100 text-emerald-700",
  browsing: "bg-indigo-100 text-indigo-700",
  touring: "bg-purple-100 text-purple-700",
  touring_confirmed: "bg-violet-100 text-violet-700",
  applied: "bg-amber-100 text-amber-700",
  leased: "bg-green-100 text-green-700",
};

const rowBg = (lead) => {
  if (lead.intent_score >= 7 && lead.attribution_status !== "protected") return "bg-red-50/60 border-l-4 border-l-red-400";
  if (lead.attribution_status === "at_risk") return "bg-red-50/40 border-l-4 border-l-red-300";
  if (!["protected", "reminded"].includes(lead.attribution_status) && lead.stage !== "new") return "bg-amber-50/40 border-l-4 border-l-amber-300";
  if (lead.attribution_status === "protected") return "bg-green-50/30 border-l-4 border-l-green-400";
  return "border-l-4 border-l-transparent";
};

const timeAgo = (iso) => {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

// ─── KPI Card ──────────────────────────────────────
const KPI = ({ icon: Icon, label, value, accent }) => (
  <div className={`rounded-xl border p-5 ${accent}`} data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-center justify-between mb-2">
      <Icon className="w-5 h-5 opacity-60" />
    </div>
    <p className="text-3xl font-bold">{value}</p>
    <p className="text-xs opacity-70 mt-1">{label}</p>
  </div>
);

// ─── SMS Modal ─────────────────────────────────────
const SMSModal = ({ lead, onClose, token }) => {
  const { toast } = useToast();
  const first = (lead?.name || "").split(" ")[0] || "there";
  const [msg, setMsg] = useState(
    `Hey ${first} \u2014 quick reminder before you tour:\nlist ${BRAND.agentAttribution} so I can track everything \uD83D\uDC4D`
  );
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/drip-dashboard/send-sms`, { lead_id: lead.id, message: msg }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: "SMS sent" });
      onClose(true);
    } catch {
      toast({ title: "SMS failed", variant: "destructive" });
    }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={() => onClose(false)} />
      <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="sms-modal">
        <div className="bg-[#0A2342] px-6 py-4 flex items-center justify-between">
          <h3 className="text-white font-semibold flex items-center gap-2"><MessageCircle className="w-4 h-4" /> Send SMS</h3>
          <button onClick={() => onClose(false)} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <p className="text-sm text-slate-500 mb-1">To: <strong>{lead.name}</strong> ({lead.phone})</p>
          </div>
          <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={5}
            className="textarea-field text-sm" data-testid="sms-modal-text" />
          <button onClick={handleSend} disabled={sending || !msg.trim()}
            className="w-full py-3 bg-[#DE6E4B] text-white rounded-lg font-medium hover:bg-[#c5593d] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="sms-modal-send">
            {sending ? <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : <><Send className="w-4 h-4" /> Send SMS</>}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// ─── Detail Panel ──────────────────────────────────
const DetailPanel = ({ lead, onClose, onToggleDrip }) => {
  const tlSteps = [
    { key: "Day 0", label: "Conversation Start" },
    { key: "Day 1", label: "Curiosity Hook" },
    { key: "Day 2", label: "Push to Action" },
    { key: "Day 3", label: "Decision Point" },
  ];

  const sentDays = new Set(lead.timeline?.filter((t) => t.status === "sent").map((t) => t.day_label) || []);
  const scheduledDays = new Set(lead.timeline?.filter((t) => t.status === "scheduled").map((t) => t.day_label) || []);
  const pausedDays = new Set(lead.timeline?.filter((t) => t.status === "paused").map((t) => t.day_label) || []);
  const cancelledDays = new Set(lead.timeline?.filter((t) => t.status === "cancelled").map((t) => t.day_label) || []);

  return (
    <motion.tr initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
      <td colSpan={9} className="p-0">
        <div className="bg-slate-50 border-t border-b border-slate-200 p-6" data-testid={`detail-panel-${lead.id}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h4 className="font-semibold text-[#0A2342]">Drip Timeline — {lead.name}</h4>
              {lead.drip_replied && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded-full">
                  <Reply className="w-3 h-3" /> REPLIED — Drip Stopped
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={onToggleDrip}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  lead.drip_enabled ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-red-100 text-red-700 hover:bg-red-200"
                }`} data-testid={`toggle-drip-${lead.id}`}>
                <Power className="w-3.5 h-3.5" /> {lead.drip_enabled ? "Drip ON" : "Drip OFF"}
              </button>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
            </div>
          </div>

          {/* Timeline */}
          <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-2">
            {tlSteps.map((step, i) => {
              const sent = sentDays.has(step.key) || step.key === "Day 0";
              const scheduled = scheduledDays.has(step.key);
              const paused = pausedDays.has(step.key);
              const cancelled = cancelledDays.has(step.key);
              return (
                <div key={step.key} className="flex items-center gap-1">
                  <div className={`rounded-lg px-4 py-2.5 text-center min-w-[100px] border ${
                    sent ? "bg-green-100 border-green-300 text-green-700" :
                    cancelled ? "bg-red-50 border-red-200 text-red-500" :
                    paused ? "bg-amber-100 border-amber-300 text-amber-700" :
                    scheduled ? "bg-blue-50 border-blue-200 text-blue-600" :
                    "bg-slate-100 border-slate-200 text-slate-400"
                  }`}>
                    <p className="text-xs font-semibold">{step.key}</p>
                    <p className="text-[10px] mt-0.5">{step.label}</p>
                    <p className="text-[10px] mt-1 font-medium">
                      {sent ? "Sent" : cancelled ? "Cancelled" : paused ? "Paused" : scheduled ? "Scheduled" : "—"}
                    </p>
                  </div>
                  {i < tlSteps.length - 1 && <div className={`w-4 h-0.5 ${sent ? "bg-green-400" : "bg-slate-200"}`} />}
                </div>
              );
            })}
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg p-3 border border-slate-200">
              <div className="flex items-center gap-1.5 text-slate-500 text-xs mb-1"><Eye className="w-3 h-3" /> Views</div>
              <p className="text-lg font-bold text-[#0A2342]">{lead.property_views}</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-slate-200">
              <div className="flex items-center gap-1.5 text-slate-500 text-xs mb-1"><Heart className="w-3 h-3" /> Favorites</div>
              <p className="text-lg font-bold text-[#0A2342]">{lead.favorites_count}</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-slate-200">
              <div className="flex items-center gap-1.5 text-slate-500 text-xs mb-1"><TrendingUp className="w-3 h-3" /> Intent</div>
              <p className="text-lg font-bold text-[#0A2342]">{lead.intent_score}</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-slate-200">
              <div className="flex items-center gap-1.5 text-slate-500 text-xs mb-1"><Home className="w-3 h-3" /> Matched</div>
              <p className="text-lg font-bold text-[#0A2342]">{lead.deal_ids?.length || 0}</p>
            </div>
          </div>
        </div>
      </td>
    </motion.tr>
  );
};

// ─── MAIN COMPONENT ────────────────────────────────
const DripDashboard = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [smsLead, setSmsLead] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [actioning, setActioning] = useState(null);
  const [search, setSearch] = useState("");

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    try {
      const url = filter ? `${API}/drip-dashboard?filter_type=${filter}` : `${API}/drip-dashboard`;
      const res = await axios.get(url, { headers });
      setData(res.data);
    } catch (e) {
      console.error("Drip dashboard fetch failed:", e);
    }
    setLoading(false);
  }, [filter, headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const doAction = async (action, leadId, label) => {
    setActioning(leadId + action);
    try {
      await axios.post(`${API}/drip-dashboard/${action}/${leadId}`, {}, { headers });
      toast({ title: label || "Done" });
      fetchData();
    } catch {
      toast({ title: "Action failed", variant: "destructive" });
    }
    setActioning(null);
  };

  const doBulk = async (action) => {
    if (!selected.size) return;
    setActioning("bulk");
    try {
      await axios.post(`${API}/drip-dashboard/bulk-action?action=${action}`, [...selected], { headers });
      toast({ title: `Bulk ${action}: ${selected.size} leads` });
      setSelected(new Set());
      fetchData();
    } catch {
      toast({ title: "Bulk action failed", variant: "destructive" });
    }
    setActioning(null);
  };

  const toggleDrip = async (leadId) => {
    try {
      const res = await axios.post(`${API}/drip-dashboard/toggle-drip/${leadId}`, {}, { headers });
      toast({ title: res.data.drip_enabled ? "Drip Enabled" : "Drip Disabled" });
      fetchData();
    } catch {
      toast({ title: "Toggle failed", variant: "destructive" });
    }
  };

  const toggleSelect = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const selectAll = () => {
    if (selected.size === (filteredLeads?.length || 0)) setSelected(new Set());
    else setSelected(new Set(filteredLeads.map((l) => l.id)));
  };

  const filteredLeads = data?.leads?.filter((l) =>
    !search || l.name.toLowerCase().includes(search.toLowerCase()) || l.phone?.includes(search) || l.email?.toLowerCase().includes(search.toLowerCase())
  ) || [];

  if (loading) {
    return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2342]" /></div>;
  }

  const kpis = data?.kpis || {};

  return (
    <div className="space-y-6" data-testid="drip-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#0A2342] font-['Playfair_Display']" data-testid="drip-dashboard-title">
            Drip Command Center
          </h1>
          <p className="text-slate-500 text-sm">Pipeline control &middot; Attribution protection &middot; Revenue tracking</p>
        </div>
        <button onClick={() => { setLoading(true); fetchData(); }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50" data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* KPI Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <KPI icon={Activity} label="Active Drips" value={kpis.active_drips || 0} accent="bg-white border-slate-200 text-[#0A2342]" />
        <KPI icon={Reply} label="Replies" value={kpis.replied_count || 0} accent="bg-emerald-50 border-emerald-200 text-emerald-700" />
        <KPI icon={Flame} label="High Intent" value={kpis.high_intent || 0} accent="bg-orange-50 border-orange-200 text-orange-700" />
        <KPI icon={MapPin} label="Tours (Week)" value={kpis.touring_week || 0} accent="bg-purple-50 border-purple-200 text-purple-700" />
        <KPI icon={CheckCircle} label="Applied (Week)" value={kpis.applied_week || 0} accent="bg-green-50 border-green-200 text-green-700" />
        <KPI icon={ShieldAlert} label="High Risk" value={kpis.high_risk || 0} accent="bg-red-50 border-red-200 text-red-700" />
        <KPI icon={MailOpen} label="Open Rate" value={`${kpis.email_open_rate || 0}%`} accent="bg-blue-50 border-blue-200 text-blue-700" />
        <KPI icon={MousePointerClick} label="Click Rate" value={`${kpis.email_click_rate || 0}%`} accent="bg-violet-50 border-violet-200 text-violet-700" />
      </div>

      {/* Filters + Search + Bulk */}
      <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
        <div className="flex flex-wrap gap-2" data-testid="drip-filters">
          {[
            { key: null, label: "All Leads" },
            { key: "high_risk", label: "High Risk", icon: ShieldAlert },
            { key: "high_intent", label: "High Intent", icon: Flame },
            { key: "active", label: "Active Drips", icon: Activity },
            { key: "completed", label: "Completed", icon: CheckCircle },
          ].map((f) => (
            <button key={f.key || "all"} onClick={() => { setFilter(f.key); setLoading(true); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                filter === f.key ? "bg-[#0A2342] text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`} data-testid={`filter-${f.key || "all"}`}>
              {f.icon && <f.icon className="w-3 h-3" />} {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 w-full md:w-auto">
          <div className="relative flex-1 md:w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search leads..."
              className="input-field pl-9 text-sm" data-testid="drip-search" />
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selected.size > 0 && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-[#0A2342] rounded-xl p-3 flex flex-wrap items-center gap-3 text-white text-sm" data-testid="bulk-actions">
          <span className="font-medium">{selected.size} selected</span>
          <div className="flex gap-2 ml-auto">
            <button onClick={() => doBulk("send_reminder")} disabled={actioning === "bulk"}
              className="px-3 py-1.5 bg-white/10 rounded-lg hover:bg-white/20 text-xs font-medium flex items-center gap-1" data-testid="bulk-reminder">
              <MessageCircle className="w-3 h-3" /> Send Reminder
            </button>
            <button onClick={() => doBulk("pause")} disabled={actioning === "bulk"}
              className="px-3 py-1.5 bg-white/10 rounded-lg hover:bg-white/20 text-xs font-medium flex items-center gap-1" data-testid="bulk-pause">
              <Pause className="w-3 h-3" /> Pause Drips
            </button>
            <button onClick={() => doBulk("resend_matches")} disabled={actioning === "bulk"}
              className="px-3 py-1.5 bg-white/10 rounded-lg hover:bg-white/20 text-xs font-medium flex items-center gap-1" data-testid="bulk-resend">
              <RefreshCw className="w-3 h-3" /> Resend Matches
            </button>
            <button onClick={() => setSelected(new Set())} className="px-3 py-1.5 text-xs text-white/60 hover:text-white">Clear</button>
          </div>
        </motion.div>
      )}

      {/* Alert Badges */}
      {data?.leads?.some((l) => l.attribution_status === "at_risk") && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-sm text-red-700" data-testid="alert-high-risk">
          <ShieldAlert className="w-5 h-5 shrink-0" />
          <span className="font-medium">{data.leads.filter((l) => l.attribution_status === "at_risk").length} lead(s) touring without attribution</span>
          <button onClick={() => { setFilter("high_risk"); setLoading(true); }} className="ml-auto text-xs font-medium underline">View</button>
        </div>
      )}
      {data?.leads?.some((l) => l.intent_score >= 7 && l.attribution_status !== "protected") && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 flex items-center gap-2 text-sm text-orange-700" data-testid="alert-hot-lead">
          <Flame className="w-5 h-5 shrink-0" />
          <span className="font-medium">{data.leads.filter((l) => l.intent_score >= 7 && l.attribution_status !== "protected").length} high-intent lead(s) need attention</span>
          <button onClick={() => { setFilter("high_intent"); setLoading(true); }} className="ml-auto text-xs font-medium underline">View</button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="drip-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="w-10 p-3"><input type="checkbox" onChange={selectAll} checked={selected.size > 0 && selected.size === filteredLeads.length} data-testid="select-all" /></th>
                <th className="text-left p-3 font-medium text-slate-600">Client</th>
                <th className="text-left p-3 font-medium text-slate-600 hidden sm:table-cell">Phone</th>
                <th className="text-left p-3 font-medium text-slate-600">Stage</th>
                <th className="text-left p-3 font-medium text-slate-600">Drip</th>
                <th className="text-left p-3 font-medium text-slate-600">Attribution</th>
                <th className="text-left p-3 font-medium text-slate-600">Intent</th>
                <th className="text-left p-3 font-medium text-slate-600 hidden lg:table-cell">Last Activity</th>
                <th className="text-right p-3 font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <AnimatePresence>
              {filteredLeads.length === 0 ? (
                <tbody>
                  <tr><td colSpan={9} className="p-12 text-center text-slate-400">
                    <Activity className="w-10 h-10 mx-auto mb-3 opacity-30" />
                    <p>No concierge leads {filter ? "match this filter" : "yet"}</p>
                  </td></tr>
                </tbody>
              ) : (
                filteredLeads.map((lead) => {
                  const attr = attrConfig[lead.attribution_status] || attrConfig.not_acknowledged;
                  const AttrIcon = attr.icon;
                  const isExpanded = expandedId === lead.id;
                  return (
                    <motion.tbody key={lead.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                      <tr className={`border-b border-slate-100 hover:bg-slate-50/50 transition-colors cursor-pointer ${rowBg(lead)}`}
                        onClick={() => setExpandedId(isExpanded ? null : lead.id)} data-testid={`drip-row-${lead.id}`}>
                        <td className="p-3" onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" checked={selected.has(lead.id)} onChange={() => toggleSelect(lead.id)} />
                        </td>
                        <td className="p-3">
                          <p className="font-medium text-[#0A2342] truncate max-w-[160px]">{lead.name}</p>
                          <p className="text-xs text-slate-400 truncate max-w-[160px]">{lead.email}</p>
                        </td>
                        <td className="p-3 hidden sm:table-cell">
                          <span className="text-slate-600 text-xs">{lead.phone || "—"}</span>
                        </td>
                        <td className="p-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${stageColors[lead.stage] || "bg-slate-100 text-slate-600"}`}>
                            {lead.stage}
                          </span>
                        </td>
                        <td className="p-3">
                          {lead.drip_replied ? (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                              <Reply className="w-3 h-3" /> Replied
                            </span>
                          ) : !lead.drip_enabled ? (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-red-500">
                              <Power className="w-3 h-3" /> Off
                            </span>
                          ) : (
                            <span className={`text-xs font-medium ${lead.drip_paused ? "text-amber-600" : "text-[#0A2342]"}`}>
                              {lead.drip_paused ? "Paused" : lead.drip_day}
                            </span>
                          )}
                        </td>
                        <td className="p-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${attr.color}`}>
                            <AttrIcon className="w-3 h-3" /> <span className="hidden xl:inline">{attr.label}</span>
                          </span>
                        </td>
                        <td className="p-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${intentColor(lead.intent_score)}`}>
                            {lead.intent_score >= 7 && <Flame className="w-3 h-3" />}
                            {lead.intent_score} <span className="hidden xl:inline">({intentLabel(lead.intent_score)})</span>
                          </span>
                        </td>
                        <td className="p-3 hidden lg:table-cell">
                          <p className="text-xs text-slate-500">{lead.last_sms_type ? `${lead.last_sms_type} SMS` : "—"}</p>
                          <p className="text-[10px] text-slate-400">{timeAgo(lead.last_sms_at || lead.updated_at)}</p>
                        </td>
                        <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            {lead.phone && (
                              <button onClick={() => setSmsLead(lead)} title="Send SMS"
                                className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-500" data-testid={`action-sms-${lead.id}`}>
                                <MessageCircle className="w-4 h-4" />
                              </button>
                            )}
                            <button onClick={() => doAction("resend-matches", lead.id, "Matches resent")} title="Resend Matches"
                              disabled={actioning === lead.id + "resend-matches"}
                              className="p-1.5 rounded-lg hover:bg-purple-50 text-purple-500 disabled:opacity-30" data-testid={`action-resend-${lead.id}`}>
                              <RefreshCw className={`w-4 h-4 ${actioning === lead.id + "resend-matches" ? "animate-spin" : ""}`} />
                            </button>
                            {lead.stage !== "touring" && (
                              <button onClick={() => doAction("mark-touring", lead.id, "Marked touring")} title="Mark Touring"
                                disabled={actioning === lead.id + "mark-touring"}
                                className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-500 disabled:opacity-30" data-testid={`action-touring-${lead.id}`}>
                                <MapPin className="w-4 h-4" />
                              </button>
                            )}
                            {lead.attribution_status !== "protected" && (
                              <button onClick={() => doAction("confirm-attribution", lead.id, "Attribution confirmed")} title="Confirm Attribution"
                                disabled={actioning === lead.id + "confirm-attribution"}
                                className="p-1.5 rounded-lg hover:bg-green-50 text-green-500 disabled:opacity-30" data-testid={`action-confirm-${lead.id}`}>
                                <ShieldCheck className="w-4 h-4" />
                              </button>
                            )}
                            {!lead.drip_paused && lead.drip_day !== "Completed" && (
                              <button onClick={() => doAction("pause", lead.id, "Drip paused")} title="Pause Drip"
                                disabled={actioning === lead.id + "pause"}
                                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-30" data-testid={`action-pause-${lead.id}`}>
                                <Pause className="w-4 h-4" />
                              </button>
                            )}
                            {lead.drip_paused && (
                              <button onClick={() => doAction("resume", lead.id, "Drip resumed")} title="Resume Drip"
                                disabled={actioning === lead.id + "resume"}
                                className="p-1.5 rounded-lg hover:bg-green-50 text-green-600 disabled:opacity-30" data-testid={`action-resume-${lead.id}`}>
                                <Play className="w-4 h-4" />
                              </button>
                            )}
                            <button onClick={() => setExpandedId(isExpanded ? null : lead.id)} title="Details"
                              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && <DetailPanel lead={lead} onClose={() => setExpandedId(null)} onToggleDrip={() => toggleDrip(lead.id)} />}
                    </motion.tbody>
                  );
                })
              )}
            </AnimatePresence>
          </table>
        </div>
      </div>

      {/* SMS Modal */}
      {smsLead && <SMSModal lead={smsLead} token={token} onClose={(sent) => { setSmsLead(null); if (sent) fetchData(); }} />}
    </div>
  );
};

export default DripDashboard;
